import React, { useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import UnderlineExtension from '@tiptap/extension-underline';
import ImageExtension from '@tiptap/extension-image';
import {
  Check, X, Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Heading2, Heading3, ImagePlus, Undo2, Redo2, Quote
} from 'lucide-react';

// Convert plain text / markdown-ish content to basic HTML for the editor
const textToHtml = (text) => {
  if (!text) return '';
  // If it already looks like HTML, return as-is
  if (text.trim().startsWith('<')) return text;

  const lines = text.split('\n');
  let html = '';
  let inList = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      if (inList) { html += '</ul>'; inList = false; }
      continue;
    }
    if (trimmed.startsWith('### ')) {
      if (inList) { html += '</ul>'; inList = false; }
      html += `<h3>${trimmed.slice(4)}</h3>`;
    } else if (trimmed.startsWith('## ')) {
      if (inList) { html += '</ul>'; inList = false; }
      html += `<h2>${trimmed.slice(3)}</h2>`;
    } else if (trimmed.startsWith('# ')) {
      if (inList) { html += '</ul>'; inList = false; }
      html += `<h2>${trimmed.slice(2)}</h2>`;
    } else if (trimmed.match(/^[-•*]\s/)) {
      if (!inList) { html += '<ul>'; inList = true; }
      html += `<li>${trimmed.replace(/^[-•*]\s*/, '')}</li>`;
    } else if (trimmed.match(/^\d+\.\s/)) {
      if (inList) { html += '</ul>'; inList = false; }
      html += `<li>${trimmed.replace(/^\d+\.\s*/, '')}</li>`;
    } else {
      if (inList) { html += '</ul>'; inList = false; }
      html += `<p>${trimmed}</p>`;
    }
  }
  if (inList) html += '</ul>';
  return html;
};

// Convert editor HTML back to clean text for storage
const htmlToText = (html) => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;

  const walk = (node) => {
    let text = '';
    for (const child of node.childNodes) {
      if (child.nodeType === 3) { // text node
        text += child.textContent;
      } else if (child.nodeType === 1) { // element
        const tag = child.tagName.toLowerCase();
        if (tag === 'h2') text += `## ${walk(child)}\n`;
        else if (tag === 'h3') text += `### ${walk(child)}\n`;
        else if (tag === 'p') text += `${walk(child)}\n`;
        else if (tag === 'li') text += `- ${walk(child)}\n`;
        else if (tag === 'ul' || tag === 'ol') text += walk(child);
        else if (tag === 'strong' || tag === 'b') text += `**${walk(child)}**`;
        else if (tag === 'em' || tag === 'i') text += `*${walk(child)}*`;
        else if (tag === 'u') text += walk(child);
        else if (tag === 'br') text += '\n';
        else if (tag === 'img') text += `\n[Image: ${child.getAttribute('src')?.slice(0, 50)}...]\n`;
        else if (tag === 'blockquote') text += `> ${walk(child)}\n`;
        else text += walk(child);
      }
    }
    return text;
  };

  return walk(div).trim();
};

const SectionEditor = ({ isDark, content, onSave, onCancel }) => {
  const imageInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      UnderlineExtension,
      ImageExtension.configure({ inline: false, allowBase64: true }),
    ],
    content: textToHtml(content),
    editorProps: {
      attributes: {
        class: `px-4 py-3 text-sm min-h-[200px] max-h-[400px] overflow-y-auto focus:outline-none ${
          isDark ? 'text-gray-200' : 'text-gray-900'
        }`,
      },
    },
  });

  const handleImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;

    const reader = new FileReader();
    reader.onload = () => {
      editor.chain().focus().setImage({ src: reader.result }).run();
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, [editor]);

  const handleSave = () => {
    if (!editor) return;
    // Store as HTML so rich formatting is preserved
    onSave(editor.getHTML());
  };

  if (!editor) return null;

  const btn = (active) =>
    `p-1 rounded transition-colors ${
      active
        ? 'bg-blue-200 text-blue-700 dark:bg-blue-800 dark:text-blue-200'
        : isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-200'
    }`;

  return (
    <div className="mt-2 space-y-2">
      {/* Toolbar */}
      <div className={`flex items-center gap-0.5 px-2 py-1.5 rounded-t-lg border border-b-0 ${
        isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
      }`}>
        <button onClick={() => editor.chain().focus().toggleBold().run()} className={btn(editor.isActive('bold'))} title="Bold">
          <Bold size={14} />
        </button>
        <button onClick={() => editor.chain().focus().toggleItalic().run()} className={btn(editor.isActive('italic'))} title="Italic">
          <Italic size={14} />
        </button>
        <button onClick={() => editor.chain().focus().toggleUnderline().run()} className={btn(editor.isActive('underline'))} title="Underline">
          <UnderlineIcon size={14} />
        </button>
        <div className={`w-px h-4 mx-1 ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} />
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={btn(editor.isActive('heading', { level: 2 }))} title="Heading">
          <Heading2 size={14} />
        </button>
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={btn(editor.isActive('heading', { level: 3 }))} title="Sub-heading">
          <Heading3 size={14} />
        </button>
        <div className={`w-px h-4 mx-1 ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} />
        <button onClick={() => editor.chain().focus().toggleBulletList().run()} className={btn(editor.isActive('bulletList'))} title="Bullet list">
          <List size={14} />
        </button>
        <button onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btn(editor.isActive('orderedList'))} title="Numbered list">
          <ListOrdered size={14} />
        </button>
        <button onClick={() => editor.chain().focus().toggleBlockquote().run()} className={btn(editor.isActive('blockquote'))} title="Quote">
          <Quote size={14} />
        </button>
        <div className={`w-px h-4 mx-1 ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} />
        <button onClick={() => imageInputRef.current?.click()} className={btn(false)} title="Insert image">
          <ImagePlus size={14} />
        </button>
        <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
        <div className="flex-1" />
        <button onClick={() => editor.chain().focus().undo().run()} className={btn(false)} title="Undo">
          <Undo2 size={14} />
        </button>
        <button onClick={() => editor.chain().focus().redo().run()} className={btn(false)} title="Redo">
          <Redo2 size={14} />
        </button>
      </div>

      {/* Editor */}
      <div className={`rounded-b-lg border ${
        isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
      } [&_.ProseMirror]:min-h-[200px] [&_.ProseMirror_h2]:text-lg [&_.ProseMirror_h2]:font-semibold [&_.ProseMirror_h2]:mt-3 [&_.ProseMirror_h2]:mb-1 [&_.ProseMirror_h3]:text-base [&_.ProseMirror_h3]:font-semibold [&_.ProseMirror_h3]:mt-2 [&_.ProseMirror_h3]:mb-1 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-5 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-5 [&_.ProseMirror_blockquote]:border-l-4 [&_.ProseMirror_blockquote]:pl-3 [&_.ProseMirror_blockquote]:italic [&_.ProseMirror_blockquote]:opacity-80 [&_.ProseMirror_img]:max-w-full [&_.ProseMirror_img]:rounded-lg [&_.ProseMirror_img]:my-2`}>
        <EditorContent editor={editor} />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button onClick={handleSave}
          className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-green-500 text-white hover:bg-green-600 transition-colors">
          <Check size={12} /> Save
        </button>
        <button onClick={onCancel}
          className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg transition-colors ${
            isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
          }`}>
          <X size={12} /> Cancel
        </button>
      </div>
    </div>
  );
};

export default SectionEditor;
